<script setup lang="ts">
import { useAttrs, computed } from 'vue';
import NumberField from './NumberField.vue';

defineOptions({
    name: 'IdeoFormNumber',
    inheritAttrs: false
});

const { $i18n } = useLocalization();

const model = defineModel<number | null>();

const props = withDefaults(defineProps<{
    locale?: string;
    disabled?: boolean;
    readonly?: boolean;
    size?: 'sm' | 'md' | 'lg';
    fluid?: boolean;
    clearable?: boolean;
    digitsOnly?: boolean;
    min?: number | null;
    max?: number | null;
    suffix?: string;
    minfractiondigits?: number;
    maxfractiondigits?: number;
}>(), {
    locale: 'en-US',
    disabled: false,
    readonly: false,
    size: 'md',
    fluid: true,
    clearable: false,
    digitsOnly: false,
    suffix: undefined,
    minfractiondigits: 0,
    maxfractiondigits: 2
});

const emit = defineEmits<{
    (e: 'input', value: number): void;
}>();

const root = ref<HTMLElement | null>(null);
const generatedId = useId();
const displayValue = ref('');
const isFocused = ref(false);

let broadcasting = false;
let typing = false;
let externalText = false;

const attrs = useAttrs() as any;
const locale = computed(() => props.locale ?? $i18n.locale());
const inputId = computed(() => attrs.inputId ?? attrs['input-id'] ?? attrs.id ?? generatedId);
const minFractionDigits = computed(() => Number(attrs.minFractionDigits ?? attrs['min-fraction-digits'] ?? props.minfractiondigits));
const maxFractionDigits = computed(() => Number(attrs.maxFractionDigits ?? attrs['max-fraction-digits'] ?? props.maxfractiondigits));

function getMinConstraint()
{
    if (props.min === null)
    {
        return null;
    }

    if (props.min !== undefined)
    {
        return props.min;
    }

    return 0;
}

const inputAttrs = computed(() =>
{
    const rest = { ...attrs };

    delete rest.id;
    delete rest.inputId;
    delete rest['input-id'];
    delete rest.minFractionDigits;
    delete rest['min-fraction-digits'];
    delete rest.maxFractionDigits;
    delete rest['max-fraction-digits'];
    delete rest.type;
    delete rest.class;
    delete rest.style;

    return rest;
});

const formatter = computed(() => new Intl.NumberFormat(locale.value, {
    minimumFractionDigits: minFractionDigits.value,
    maximumFractionDigits: maxFractionDigits.value,
    useGrouping: !props.digitsOnly
}));

function resetValue()
{
    if (isInactive()) return;
    const minConstraint = getMinConstraint();

    model.value = minConstraint === null ? null : minConstraint;
    displayValue.value = formatValue(model.value);
}

function escapeRegExp(value: string)
{
    return value.replaceAll(/[-[\]{}()*+?.,\\^$|#\s]/g, '\\$&');
}

function getNumberPart(type: Intl.NumberFormatPartTypes)
{
    return Intl.NumberFormat(locale.value).formatToParts(1000.1).find(part => part.type === type)?.value ?? '';
}

function parseValue(value: string)
{
    const groupSeparator = getNumberPart('group');
    const decimalSeparator = getNumberPart('decimal') || '.';
    let normalized = value.replaceAll(/\s/g, '');

    if (groupSeparator)
    {
        normalized = normalized.replaceAll(new RegExp(escapeRegExp(groupSeparator), 'g'), '');
    }

    normalized = normalized
        .replaceAll(new RegExp(escapeRegExp(decimalSeparator), 'g'), '.')
        .replaceAll(/[^\d.-]/g, '');

    if (!normalized || normalized === '-')
    {
        return null;
    }

    const parsed = Number(normalized);

    return Number.isNaN(parsed) ? null : parsed;
}

function normalizeValue(value: number | null)
{
    const minConstraint = getMinConstraint();

    if (value === null)
    {
        return minConstraint === null ? null : minConstraint;
    }

    if (minConstraint !== null && minConstraint !== undefined && value < minConstraint)
    {
        return minConstraint;
    }

    if (props.max !== null && props.max !== undefined && value > props.max)
    {
        return props.max;
    }

    return value;
}

function formatValue(value: number | null)
{
    if (value === null || value === undefined)
    {
        return '';
    }

    return formatter.value.format(value);
}

function handleBeforeInput(event: InputEvent)
{
    if (!props.digitsOnly || event.isComposing) return;

    const text = event.data ?? event.dataTransfer?.getData('text/plain');
    if (text && /[^0-9]/.test(text)) event.preventDefault();
}

function sanitizeDigitsInput(input: HTMLInputElement)
{
    if (!props.digitsOnly || /^[0-9]*$/.test(input.value)) return;

    const cursor = input.selectionStart;
    const digitsBeforeCursor = cursor === null ? null : input.value.slice(0, cursor).replaceAll(/[^0-9]/g, '').length;
    input.value = input.value.replaceAll(/[^0-9]/g, '');
    displayValue.value = input.value;
    if (digitsBeforeCursor !== null) input.setSelectionRange(digitsBeforeCursor, digitsBeforeCursor);
}

function handleInput(event: Event)
{
    if (broadcasting)
    {
        emit('input', model.value as number);
        return;
    }

    if (isInactive())
    {
        displayValue.value = formatValue(model.value);
        return;
    }
    const input = event.target as HTMLInputElement;
    sanitizeDigitsInput(input);
    const value = parseValue(input.value);
    const normalized = normalizeValue(value);

    externalText = false;
    typing = true;
    model.value = normalized;
    // Zmiana modelu w tym samym odświeżeniu co wpis pochodzi z klawiatury, także gdy rodzic przekształcił ją w odpowiedzi na wpis.
    nextTick(() =>
    {
        typing = false;
    });
    emit('input', normalized as number);
}

function handleBlur()
{
    isFocused.value = false;

    // Tekst ustawiony przez obserwator jest już obrazem modelu - ponowne parsowanie przycięłoby model do miejsc po przecinku z formatu.
    const showsModel = externalText;

    externalText = false;
    if (isInactive() || showsModel) return;
    model.value = normalizeValue(parseValue(displayValue.value));
    displayValue.value = formatValue(model.value);
}

function handleFocus()
{
    isFocused.value = true;
}

const stepSize = computed(() =>
{
    const value = Number(attrs.step ?? 1);
    return Number.isFinite(value) && value > 0 ? value : 1;
});
const decrementDisabled = computed(() => model.value != null && getMinConstraint() != null && model.value <= getMinConstraint()!);
const incrementDisabled = computed(() => model.value != null && props.max != null && model.value >= props.max);

function isInactive()
{
    return props.disabled || props.readonly || root.value?.querySelector('input')?.matches(':disabled') === true;
}

function decimalPlaces(value: number)
{
    const [coefficient, exponent = '0'] = String(value).toLowerCase().split('e');
    return Math.max(0, (coefficient.split('.')[1]?.length ?? 0) - Number(exponent));
}

function stepValue(direction: -1 | 1)
{
    if (isInactive()) return;
    const current = (isFocused.value ? parseValue(displayValue.value) : model.value) ?? getMinConstraint() ?? 0;
    const precision = Math.min(15, Math.max(decimalPlaces(current), decimalPlaces(stepSize.value)));
    const next = normalizeValue(Number((current + direction * stepSize.value).toFixed(precision)));
    if (Object.is(next, model.value)) return;
    const input = root.value?.querySelector('input');
    if (!input) return;
    displayValue.value = formatValue(next);
    input.value = displayValue.value;
    input.dispatchEvent(new Event('input', { bubbles: true }));
    input.dispatchEvent(new Event('change', { bubbles: true }));
}

function handleKeydown(event: KeyboardEvent)
{
    if (event.key !== 'ArrowUp' && event.key !== 'ArrowDown') return;
    event.preventDefault();
    stepValue(event.key === 'ArrowUp' ? 1 : -1);
}

function broadcastInput()
{
    const text = displayValue.value;

    broadcasting = true;
    try
    {
        root.value?.querySelector('input')?.dispatchEvent(new Event('input', { bubbles: true }));
    }
    finally
    {
        broadcasting = false;
        // `v-model` pola czyta przy rozgłoszeniu tekst sprzed odświeżenia DOM i odsyła go, gdy różni się od ostatniego wpisu z klawiatury - bez tego drugi zapis spoza pola zostawiał na ekranie poprzednią wartość.
        displayValue.value = text;
    }
}

watch(model, () =>
{
    // Pole z ogniskiem zachowuje tekst tylko przy wpisie z klawiatury, bo formatowanie usuwałoby niedokończony tekst („12,", „-"). Zapis spoza pola musi być widoczny, inaczej opuszczenie pola przeparsuje stary tekst i cofnie zapis.
    if (!isFocused.value || !typing)
    {
        displayValue.value = formatValue(model.value);
        externalText = isFocused.value;
    }
    broadcastInput();
}, {
    immediate: true
});
</script>

<template>
    <div ref="root" :class="attrs.class" :style="attrs.style">
        <NumberField :input-id="inputId" :aria-label="attrs['aria-label']" :disabled="props.disabled" :readonly="props.readonly" :decrement-disabled="decrementDisabled" :increment-disabled="incrementDisabled" :size="props.size" :fluid="props.fluid" @step="stepValue">
            <IdeoFormInput
                :id="inputId"
                v-model="displayValue"
                v-bind="inputAttrs"
                :fluid="fluid"
                :disabled="props.disabled"
                :readonly="props.readonly"
                :size="props.size"
                type="text"
                :inputmode="props.digitsOnly ? 'numeric' : 'decimal'"
                @beforeinput="handleBeforeInput"
                role="spinbutton"
                :aria-valuemin="getMinConstraint() ?? undefined"
                :aria-valuemax="props.max ?? undefined"
                :aria-valuenow="model ?? undefined"
                @input="handleInput"
                @focus="handleFocus"
                @blur="handleBlur"
                @keydown="handleKeydown"
            />
            <template v-if="props.suffix || props.clearable" #append>
                <span v-if="props.suffix" class="shrink-0 px-[8px] text-ui-helper text-fg-muted dark:text-fg-muted-dark">{{ props.suffix }}</span>
                <IdeoAction v-if="props.clearable" display="toolbar" icon="x" :disabled="props.disabled || props.readonly" :aria-label="$t('[[[Wyczyść wartość]]]')" @click.prevent="resetValue" />
            </template>
        </NumberField>
    </div>
</template>
